/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/forbid-dom-props */
/* eslint-disable max-len */
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import {
    Button,
    ButtonSize,
    ButtonVariant,
    FormSelect,
    HttpStatus,
    PublicLayout,
} from '@catalyze/ui';
import type { IProductsInfo } from 'api/products/getProducts';
import { fetchProducts } from 'api/products/getProducts';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { Routes } from 'constants/routes';
import { StyledForm } from 'features/SignUp/components/Form';
import {
    ButtonsContainer,
    InputContainer,
} from 'features/SignUp/components/forms/AccountDetailsForm/styled';
import { useOptionsWithSearch } from 'features/SignUp/hooks/useOptionsWithSearch';
import { locationSchema } from 'schemas/signUpForm';

import {
    CardContainer,
    FormLine,
    Spacer,
    StyledHeader,
    StyledParagraph,
    StyledPlan,
} from './styled';

export const ZipCheck = () => {
    const formMethods = useForm({
        resolver: yupResolver(locationSchema),
    });
    const { getValues, setError, clearErrors, setValue } = formMethods;

    const [searchValue, setSearchValue] = useState('');
    const [isPageLoading, setIsLoading] = useState(false);
    const [count, setCount] = useState<IProductsInfo>({
        count: -1,
        re_low: -1,
        re_high: -1,
        dist_hi: [],
        dist_lo: [],
    });

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const {
        options,
        error,
        isLoading: isZipOptionsLoading,
        search,
        foundSearchOption,
        clear: clearZipOptions,
    } = useOptionsWithSearch({
        search: searchValue,
        prefetchValue: getValues()?.zip || undefined,
    });

    // Limits characters to numbers of max length
    function setValidSearchValue(test: string) {
        const val = test.replace(/[^0-9]/u, '').slice(0, 5);
        setSearchValue(val);
    }

    const onZipCodeUpdate = useCallback(
        async (zip: string) => {
            setIsLoading(true);
            await queryClient.invalidateQueries('products');
            const productData = await queryClient.fetchQuery(
                ['products', { zip }],
                () => fetchProducts(zip)
            );
            setCount(productData);
            setIsLoading(false);
        },
        [queryClient]
    );

    // Sets newly found option as value, reset errors
    useEffect(() => {
        if (foundSearchOption) {
            setValue('zip', foundSearchOption?.value);
            setValue('state', foundSearchOption?.state);
            clearErrors('zip');
            void onZipCodeUpdate(foundSearchOption?.value);
        }
    }, [foundSearchOption, setValue, clearErrors, onZipCodeUpdate]);

    // Sets error if zip is not found
    useEffect(() => {
        if (error?.response) {
            setError('zip', {
                message:
                    Number(error?.response.status || 0) === HttpStatus.NOT_FOUND
                        ? 'Zip code not found'
                        : 'Error occured, please try again',
            });
        } else {
            clearErrors('zip');
        }
    }, [error, setError, clearErrors]);

    const value = getValues().zip;

    return (
        <PublicLayout>
            <CardContainer style={{ marginBottom: 100 }}>
                <LoadingOverlay shown={isPageLoading || isZipOptionsLoading} />
                <StyledParagraph>
                    Use the below tool to check how many leads we have in your
                    area. Type in and search your zip code and we will display
                    how many leads are in your area and how far away they are.
                </StyledParagraph>
                <StyledParagraph>
                    After checking your area, {"don't"} forget to sign up and
                    purchase leads to lock in the best exclusive leads in your
                    area!
                </StyledParagraph>
                <StyledHeader>Check your zip code</StyledHeader>
                <FormProvider {...formMethods}>
                    <StyledForm>
                        <FormLine>
                            <InputContainer>
                                <FormSelect
                                    id="zip"
                                    label=" "
                                    // disabled={loading}
                                    options={options}
                                    value={value}
                                    isCreatable
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    onSelectOption={(value: any) => {
                                        const zipCode: string =
                                            typeof value === 'string'
                                                ? value
                                                : value.value;
                                        void onZipCodeUpdate(zipCode);
                                        // setValue('state', value.state);
                                    }}
                                    selectProps={{
                                        inputValue: searchValue,
                                        onInputChange: setValidSearchValue,
                                        onCreateOption: (value) => {
                                            search({ code: value });
                                        },
                                        formatCreateLabel: (value) =>
                                            `Search for ${value}`,
                                    }}
                                />
                            </InputContainer>
                            {!value ? null : (
                                <ButtonsContainer>
                                    <Button
                                        size={ButtonSize.SMALL}
                                        variant={ButtonVariant.SECONDARY}
                                        disabled={!value}
                                        onClick={clearZipOptions}
                                    >
                                        Clear
                                    </Button>
                                </ButtonsContainer>
                            )}
                        </FormLine>
                    </StyledForm>
                </FormProvider>
                <br />

                {!isPageLoading ? (
                    <>
                        {count.re_high === 0 && count.re_low === 0 ? (
                            <>
                                <p style={{ color: 'red', fontSize: 12 }}>
                                    Product count for this zip code is 0. Please
                                    try again with a different zip code
                                </p>
                                <br />
                            </>
                        ) : null}
                        {count.re_high && count.dist_hi?.length ? (
                            <StyledPlan
                                subscriptionId="re_high"
                                name="Quantity of properties over $1mm in your area"
                                propertyDistribution={count.dist_hi}
                                count={count.re_high}
                                zip={getValues().zip}
                                loading={isPageLoading}
                            />
                        ) : null}
                        {count.re_low && count.dist_lo?.length ? (
                            <StyledPlan
                                subscriptionId="re_low"
                                name="Quantity of properties under $1mm in your area"
                                propertyDistribution={count.dist_lo}
                                count={count.re_low}
                                zip={getValues().zip}
                                loading={isPageLoading}
                            />
                        ) : null}
                        {count.re_high || count.re_low ? (
                            <>
                                <Spacer />
                                <StyledParagraph>
                                    Click below to create an account and
                                    purchase your exclusive leads:
                                </StyledParagraph>
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    onClick={() => navigate(Routes.SIGN_UP)}
                                >
                                    Get started
                                </Button>
                                <Spacer />
                                <Spacer />
                                <StyledParagraph>
                                    Already have an account?
                                </StyledParagraph>
                                <StyledParagraph>
                                    Just sign in below and manage your
                                    subscription to purchase your leads:
                                </StyledParagraph>
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    onClick={() => navigate(Routes.OVERVIEW)}
                                >
                                    Log in
                                </Button>
                            </>
                        ) : null}
                    </>
                ) : null}
            </CardContainer>
        </PublicLayout>
    );
};
